import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { jaJP } from '@mui/x-data-grid';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import CheckIcon from '@mui/icons-material/Check';
import UpdateIcon from '@mui/icons-material/Update';
import { useAuth } from './../../contexts/AuthContext';
import { usePost } from './../../apis/ApiRequests';
import { CustomDataGrid } from '../../components/dataGrid/CustomDataGrid';
import CustomOutlinedInput from '../../components/input/CustomOutlinedInput';
import Header from '../../components/Header';
import Frame from '../../components/uiParts/Frame';
import TitleBar from '../../components/uiParts/TitleBar';
import CustomConfirm from '../../components/confirm/CustomConfirm';
import CustomSnackBar from '../../components/alert/CustomSnackBar';
import CustomSelect from '../../components/select/CustomSelect';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const BidManager = () => {

	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - セッション情報を取得
	//-----------------------------------------------
    const { authInfo }   = useAuth();
    const facilityCode   = authInfo.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId;
    const luserName      = authInfo.userName;
    const authority      = authInfo.authority;
    const traderCode     = authInfo.traderCode;
    const trader         = authInfo.trader;
    
    const search    = useLocation().search;
    const query     = new URLSearchParams(search);
    const bidGroupNo = query.get('bidGroupNo');

    const [ contentsHeight,        setContentsHeight        ] = useState(window.innerHeight -150);
    const [ dataGridHeight,        setDataGridHeight        ] = useState(window.innerHeight -450);
    const [ containerHeight,       setContainerHeight       ] = useState(window.innerHeight -100);
    const [ dataGridWidth,         setDataGridWidth         ] = useState(window.innerWidth -370);
    const [ searchCount,           setSearchCount           ] = useState(0);
    const [ bidHistoryList,        setBidHistoryList        ] = useState([]);
    const [ requireMessage,        setRequireMessage        ] = useState('');
    const [ checkedBidHistoryList, setCheckedBidHistoryList ] = useState([]);
    const [ groupSuryoRemain,      setGroupSuryoRemain      ] = useState('');
    const [ bidList,               setBidList               ] = useState([]);
    const [ bidPersonList,         setBidPersonList         ] = useState([]);
    const [ unitName,              setUnitName              ] = useState('Kg');

    const navigate = useNavigate();
    
    // BidGroupデータ
    const [ bidGroup, setBidGroup ] = useState({
        bidGroupNo    : '',
        entryNo       : '',
        entryDate     : '',
        bidName       : '',
        nisugata      : '',
        kikakuCode    : '',
        kikaku        : '',
        kgSuryo       : '',
        kgKubun       : '',
        suryo         : '',
        bidPersonCode : '',
        bidPerson     : '',
        groupStatus   : '',
        danbe         : '',
    });
    // UI Message
    const [ messageState, setMessageState ] = useState({
        snackBarTrue            : false,
        snackBarFalse           : false,
        require                 : false,
        confirm                 : false,
        confirm_register        : false,
        confirm_register_before : false,
        confirm_vote            : false,
        confirm_vote_release    : false
    });
    
	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
    // API - getData / BidGroup
	//-----------------------------------------------
    const { response : getData_res, loading : getData_Loading, post:getData_post } = usePost();
    const getData = async () => {
        const postData = {
            'className'    : 'mch.models.table.BidGroup',
            'methodName'   : 'getData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'bidGroupNo'   : bidGroupNo
        }
        await getData_post(postData);
    };
	//-----------------------------------------------
    // API - getDataInGroup / BidList
	//-----------------------------------------------
    const { response : getDataInGroup_res, loading : getDataInGroup_Loading, post:getDataInGroup_post } = usePost();
    const getDataInGroup = async () => {
        const postData = {
            'className'    : 'mch.models.table.BidList',
            'methodName'   : 'getDataInGroup',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'bidGroupNo'   : isRebid() ? bidGroupNo.split(".")[0] : bidGroupNo,
        }
        await getDataInGroup_post(postData);
    };
	//-----------------------------------------------
    // API - searchData / BidHistory
	//-----------------------------------------------
    const { response : searchData_history_res, loading : searchData_history_Loading, post:searchData_history_post } = usePost();
    const searchData_history = async () => {
        let methodName = 'searchData'
        if (bidGroup.groupStatus != 9) methodName = 'searchDataByAutoBid'
        if (bidGroup.kgKubun == 0) methodName = 'searchDataByAutoBidNum'
        const postData = {
            'className'    : 'mch.models.table.BidHistory',
            'methodName'   : methodName,
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'bidGroupNo'   : bidGroupNo
        }
        await searchData_history_post(postData);
    };
	//-----------------------------------------------
    // API - setData / BidHistory
	//-----------------------------------------------
    const { response : setData_res, post:setData_post } = usePost();
    const setData = async (targetData) => {
        const postData = {
            'className'     : 'mch.models.table.BidHistory',
            'methodName'    : 'setData',
            'facilityCode'  : facilityCode,
            'userId'        : userId,
            'activeFlag'    : targetData.activeFlag,
            'userId'        : targetData.userId,
            'bidGroupNo'    : targetData.bidGroupNo,
            'seq'           : targetData.seq,
            'traderCode'    : targetData.traderCode,
            'trader'        : targetData.trader,
            'entryUserId'   : targetData.entryUserId,
            'entryUser'     : targetData.entryUser,
            'ip'            : targetData.ip,
            'entrySuryo'    : targetData.entrySuryo ? targetData.entrySuryo : 0,
            'entryUnit'     : targetData.entryUnit,
            'bidSuryo'      : targetData.bidSuryo ? targetData.bidSuryo : 0,
            'tanka'         : targetData.tanka,
            'conditionCode' : targetData.conditionCode,
            'condition'     : targetData.condition,
            'bidStatus'     : targetData.bidStatus,
        }
        await setData_post(postData);
    };
	//-----------------------------------------------
    // API - updateBidGroup / BidGroup
	//-----------------------------------------------
    const { response : updateBidGroup_res, post:updateBidGroup_post } = usePost();
    const updateBidGroup = async (bidStatus) => {
        const postData = {
            'className'     : 'mch.models.table.BidGroup',
            'methodName'    : 'updateBidGroup',
            'facilityCode'  : facilityCode,
            'userId'        : userId,
            'bidGroupNo'    : bidGroupNo,
            'entryDate'     : bidGroup.entryDate,
            'bidPersonCode' : bidGroup.bidPersonCode,
            'bidPerson'     : bidGroup.bidPerson,
            'groupStatus'   : bidStatus
        }
        await updateBidGroup_post(postData);
    };
	//-----------------------------------------------
    // API - searchData / masta.BidPerson
	//-----------------------------------------------
    const { response : getDataAll_bidPerson_res, post : getDataAll_bidPerson_post } = usePost();
    const getDataAll_bidPerson = async () => {
        const postData = {
            'className'    : 'mch.models.masta.BidPerson',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'userId'       : userId
        }
        await getDataAll_bidPerson_post(postData);
    };
	//-----------------------------------------------
    // API - getData / masta.BidPerson
	//-----------------------------------------------
    const { response : getData_bidPerson_res, post : getData_bidPerson_post } = usePost();
    const getData_bidPerson = async (bidPersonCode) => {
        const postData = {
            'className'    : 'mch.models.masta.BidPerson',
            'methodName'   : 'getData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'code'         : bidPersonCode
        }
        await getData_bidPerson_post(postData);
    };
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        setSearchCount(searchCount+1);
        
        const handleResize = () => {
            setContentsHeight(window.innerHeight - 150);
            setDataGridHeight(window.innerHeight - 450);
            setContainerHeight(window.innerHeight - 100);
            setDataGridWidth(window.innerWidth - 370);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    },[]);
	//-----------------------------------------------
    // useEffect - 検索処理
	//-----------------------------------------------
    useEffect(() => {
        getData();
        getDataInGroup();
        getDataAll_bidPerson();
    }, [searchCount]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        if (getData_res) {
            setBidGroup({ ...getData_res });
            searchData_history();
        }
    }, [getData_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        setBidList([]);
        if (getDataInGroup_res) {
            setBidList(getDataInGroup_res.Data );
        }
    }, [getDataInGroup_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getDataAll / masta_BidPerson)
	//-----------------------------------------------
    useEffect(() => {
        setBidPersonList([]);
        if (getDataAll_bidPerson_res) {
            setBidPersonList(getDataAll_bidPerson_res.Data);
        }
    }, [getDataAll_bidPerson_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / masta_BidPerson)
	//-----------------------------------------------
    useEffect(() => {
        if (getData_bidPerson_res) {
            setBidGroup(prevSearchData => ({
                ...prevSearchData,
                bidPersonCode: getData_bidPerson_res.code,
                bidPerson: getData_bidPerson_res.displayName,
            }));
        }
    }, [getData_bidPerson_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        setBidHistoryList([])
        if (searchData_history_res) {
            if (authority == 3) {
                const filteredBidHistory = searchData_history_res.Data.filter(row => row.bidStatus === 1);
                setBidHistoryList(filteredBidHistory);
            } else {
                const isShowData = bidGroup.groupStatus === 8 || bidGroup.groupStatus === 9;

                const maskedData = searchData_history_res.Data.map(item => {
                    const isNoBid = item.bidStatus === 9 && item.tanka === -1; //札入なしの場合

                    return {
                    ...item,
                    entrySuryo : isNoBid ? '' :(isShowData ? item.entrySuryo : '****'),
                    tanka      : isNoBid ? '札入なし' :(isShowData ? item.tanka : '****'), 
                    condition  : isNoBid ? '' :(isShowData ? item.condition : '****'),
                    entryUnit  : isNoBid ? '' :(isShowData ? item.entryUnit : '****'),
                    bidSuryo   : isNoBid ? '' :(isShowData ? item.bidSuryo : 0),
                    bidStatus  : isNoBid ? item.bidStatus :(isShowData ? item.bidStatus : 0),
                }});

                setBidHistoryList(maskedData);

                // 同値・同一数量の入札があればメッセージを表示
                if (bidGroup.groupStatus == 8 && searchData_history_res.evenFlag) {
                    setRequireMessage('同一数量・同一単価の入札情報があります');
                    setMessageInfo('require', true);
                }
            }
        }
    }, [searchData_history_res, bidGroup]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        let maxSuryo = bidGroup.kgSuryo;
        if (bidGroup.kgKubun === 0) {
            setUnitName('箱');
            maxSuryo = bidGroup.suryo;
        }
        setGroupSuryoRemain(maxSuryo);
        if (bidHistoryList) {
            let groupSuryoAll = maxSuryo;
            bidHistoryList.map((row) => {
                groupSuryoAll = Number(groupSuryoAll) - Number(row.bidSuryo);
            })
            let remainSuryo = Number(groupSuryoAll).toFixed(2);
            remainSuryo= -0.0 ? 0.0 : Number(groupSuryoAll).toFixed(2);
            setGroupSuryoRemain(remainSuryo);
            
            // bidStatusが1の行のIDを集める
            const selectedRows = bidHistoryList.filter(row => row.bidStatus === 1).map(row => row.id);
            setCheckedBidHistoryList(selectedRows);
        }
    }, [bidHistoryList, bidGroup]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getData / unloadList)
	//-----------------------------------------------
    useEffect(() => {
        if (updateBidGroup_res) {
            setSearchCount(searchCount+1);
        }
    }, [updateBidGroup_res]);

	//===============================================
	// eventHandler
	//===============================================
	//-----------------------------------------------
    // eventHandler - 日付の変更
	//-----------------------------------------------
    const handleChange_entryDate = (value) => {
        setSearchCount(searchCount+1);
    }
	//-----------------------------------------------
    // eventHandler - 更新ボタン
	//-----------------------------------------------
    const handleClick_update = () => {
        searchData_history();
    }
	//-----------------------------------------------
    // eventHandler - セリ人の変更
	//-----------------------------------------------
    const handleChangexBidPerson = (value) => {
        if (value == 0) {
            setBidGroup(prevSearchData => ({
                ...prevSearchData,
                bidPersonCode: '',
                bidPerson: '',
            }));
        } else {
            getData_bidPerson(value);
        }
    };
	//-----------------------------------------------
    // eventHandler - 登録ボタン
	//-----------------------------------------------
    const handleClick_Register = async () => {
        const requireStatus = checkedRequire(); // 必須項目をチェックする
        if (requireStatus) {
            const updatePromises = checkedBidHistoryList.map(async (checkedBidHistoryId) => {
                const bidHistoryData = bidHistoryList.find(bidHistoryList => bidHistoryList.id === checkedBidHistoryId)
                if (bidHistoryData) {
                    const postData = {
                        'activeFlag'    : 1,
                        'bidGroupNo'    : bidHistoryData.bidGroupNo,
                        'traderCode'    : bidHistoryData.traderCode,
                        'seq'           : bidHistoryData.seq,
                        'entryUserId'   : bidHistoryData.entryUserId,
                        'trader'        : bidHistoryData.trader,
                        'ip'            : bidHistoryData.ip,
                        'entrySuryo'    : bidHistoryData.entrySuryo,
                        'entryUnit'     : bidHistoryData.entryUnit,
                        'bidSuryo'      : bidHistoryData.bidSuryo,
                        'tanka'         : bidHistoryData.tanka,
                        'conditionCode' : bidHistoryData.conditionCode,
                        'condition'     : bidHistoryData.condition,
                        'bidStatus'     : 1
                    }
                    await setData(postData);
                }
            });
            // 後処理
            try {
                await Promise.all(updatePromises);
                updateBidGroup(9);
                setSearchCount(searchCount+1);
                setMessageInfo('confirm', false);
                setMessageInfo('confirm_register', false);
                setMessageInfo('snackBarTrue', false);
                navigate('/bidGroup');
            } catch (error) {
                setMessageInfo('snackBarFalse', true);
            }
        } else {
            setMessageInfo('confirm', false);
            setMessageInfo('confirm_register', false);
            setMessageInfo('require', true);
        }
    };
	//-----------------------------------------------
    // eventHandler - 入札解除ボタンのクリック
	//-----------------------------------------------
    const handleClick_deleteGroup = async () => {
        updateBidGroup(0);
        navigate('/bidGroup');
    };
	//-----------------------------------------------
    // eventHandler - 再入札ボタンのクリック
	//-----------------------------------------------
    const handleClick_BidRestart = async () => {
        updateBidGroup(8);
        //setSearchCount(searchCount+1)
    };
	//-----------------------------------------------
    // eventHandler - 開票ボタンのクリック
	//-----------------------------------------------
    const handleClick_vote = () => {
        const isNoBid = !searchData_history_res.Data.some(item => item.bidStatus === 0);
        if (isNoBid) {
            setRequireMessage('入札が1件もありません');
            setMessageInfo('require', true);
            setMessageInfo('confirm_vote', false);
            return;
        }
        updateBidGroup(8);
        //setSearchCount(searchCount+1)
        setMessageInfo('confirm_vote', false);
    };
	//-----------------------------------------------
    // eventHandler - 開票解除ボタンのクリック
	//-----------------------------------------------
    const handleClick_vote_release = () => {
        updateBidGroup(1);
        //setSearchCount(searchCount+1)
        setMessageInfo('confirm_vote_release', false);
    };
	//===============================================
	// dataGrid
	//===============================================
	//-----------------------------------------------
    // dataGrid - 入札明細の見出し
	//-----------------------------------------------
    const columns = [
        { field: 'method', headerName: '漁法',    width: 120, columnSeparatorWidth: 1, sortable:false, },
        { field: 'senmei', headerName: '荷主',    width: 150, columnSeparatorWidth: 1, sortable:false, },
        { field: 'displayGyoshu', headerName: '品名',    width: 310, columnSeparatorWidth: 1, sortable:false, },
        { field: 'kgSuryo', headerName: 'Kg数', width: 80, columnSeparatorWidth: 1, sortable:false, type:'number'},
        { field: 'suryo', headerName: '数量', width: 55, columnSeparatorWidth: 1, sortable:false, type:'number'},
        {
            field: 'kgKubun',
            headerName: '単位',
            sortable:false,
            width: 55,
            renderCell: (params) => (
              <div>
                {unitMapping[params.value] || params.value}
              </div>
            ),
        },
        { field: 'displayBiko',    headerName: '備考',     width: 280, columnSeparatorWidth: 1, sortable:false, },
    ];
    const unitMapping = {
        1: 'Kg',
        0: '箱',
    };
	//-----------------------------------------------
    // dataGrid - 入札状況一覧の見出し
	//-----------------------------------------------
    const columns_history = [
        { field: 'id',        headerName: 'No',      width: 70, columnSeparatorWidth: 1, sortable:false, type:'number'},
        { field: 'trader',    headerName: '仲買人',  width: 150, columnSeparatorWidth: 1, sortable:false, },
        { field: 'entryUser', headerName: '入札ユーザ',  width: 150, columnSeparatorWidth: 1, sortable:false, },
        {
            field: 'tanka',
            headerName: '入札単価',
            sortable:false,
            width: 110,
            type:'number',
            renderCell: (params) => (
                params.row.bidStatus === 9 ? '札入なし' : `${params.value}円`
            ),
        },
        { field: 'entrySuryo', headerName: '入札数量', width: 120, columnSeparatorWidth: 1, sortable:false, type:'number' },
        {
            field: 'entryUnit',
            headerName: '単位',
            sortable:false,
            width: 80,
            renderCell: (params) => (
              <div>
                {bidUnitMapping[params.value] || params.value}
              </div>
            ),
        },
        { field: 'condition',  headerName: '入札条件', width: 180, columnSeparatorWidth: 1, sortable:false, },
        { field: 'bidSuryo',   headerName: '落札数量', width: 120, columnSeparatorWidth: 1, sortable:false, type:'number', editable:true},
    ];
	//-----------------------------------------------
    // dataGrid - 入札状況一覧の見出し
	//-----------------------------------------------
    const columns_history_trader = [
        { field: 'id',        headerName: 'No',      width: 70, columnSeparatorWidth: 1, type:'number'},
        { field: 'trader',    headerName: '屋号',  width: 150, columnSeparatorWidth: 1, },
        {
            field: 'tanka',
            headerName: '入札単価',
            width: 110,
            type:'number',
            renderCell: (params) => (
                `${params.value}円`
            ),
        },
        { field: 'entrySuryo', headerName: '落札数量', width: 120, columnSeparatorWidth: 1, type:'number' },
        {
            field: 'entryUnit',
            headerName: '単位',
            width: 80,
            renderCell: (params) => (
              <div>
                {bidUnitMapping[params.value] || params.value}
              </div>
            ),
        },
        { field: 'condition',  headerName: '入札条件', width: 180, columnSeparatorWidth: 1, },
    ];
    const bidUnitMapping = {
        1: '全量',
        2: 'Kg',
        3: 'D',
        4: '箱',
        5: '匹',
    };
    //-----------------------------------------------
    // dataGrid - 行の更新処理
    //-----------------------------------------------
    const handleProcessRowUpdate = (newRow, oldRow) => {
        let targetId = newRow.id;
        setBidHistoryList((prevData) => {
            return prevData.map((row) => {
                if (row.id === targetId) {
                    return newRow;
                }
                return row;
            });
        });
        return newRow;
    };
    //-----------------------------------------------
    // dataGrid - 行の選択
    //-----------------------------------------------
    const handleClick_BidHistoryList = (event) => {
        if (event.field === '__check__') {
            // bidStatus === 9 の行はチェックできない
            const clickedRow = bidHistoryList.find(item => item.id === event.id);
            if (clickedRow?.bidStatus === 9) {
              return; 
            }
            if (checkedBidHistoryList.includes(event.id)) {
                setCheckedBidHistoryList(checkedBidHistoryList.filter((checkedBidHistoryList, index) => (checkedBidHistoryList !== event.id)));
            } else {
                setCheckedBidHistoryList((checkedBidHistoryList)=>[...checkedBidHistoryList, event.id]);
            }
        }
    };
	//===============================================
	// Function
	//===============================================
	//-----------------------------------------------
	// Function - 登録前に残数量をチェックする
	//-----------------------------------------------
    const handleClick_Register_before = () => {
        const requireStatus = checkedRequire(); // 必須項目をチェックする
        if (requireStatus) {
            const num = Number(groupSuryoRemain);
            if (num > 0) {
                setMessageInfo('confirm_register_before', true);
            } else {
                setMessageInfo('confirm_register', true);
            }
        } else {
            setMessageInfo('require', true);
        }
    };
	//-----------------------------------------------
	// Function - 必須項目をチェックする
	//-----------------------------------------------
    const checkedRequire = () => {
        const errorFields = [];
        
        if (!bidGroup.bidPersonCode) errorFields.push('「登録者」');
    
        if (errorFields.length) {
            const text = errorFields.join('') + 'が入力されていません';
            setRequireMessage(text);
            return false;  // エラーがある場合は false を返します
        }
        if (!checkedBidHistoryList.length) {
            setRequireMessage('落札対象が1件も選択されていません');
            return false;  // エラーがある場合は false を返します
        }
        return true;  // エラーがない場合は true を返します
    };
    //-----------------------------------------------
	// Function - 残数再入札の判定(枝番があればtrue)
	//-----------------------------------------------
    const isRebid = () => {
        const num = Number(bidGroupNo)
        return !Number.isInteger(num);
    };
	//===============================================
	// UI Message
	//===============================================
    const setMessageInfo = (target, state) => {
        setMessageState(prevSearchData => ({
            ...prevSearchData,
            [target]: state,
        }));
    };
    //===============================================
    // Css
    //===============================================
    
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/bidGroup" setEntryDate={handleChange_entryDate} />
            <div className='bidManager bodyContens'>
                <TitleBar title="入札情報管理" />
                <div style={{ display: 'flex', height: containerHeight }} className='mainContents'>
                    <div style={{ flex: 1, padding: '0px' }}>
                        <Frame classname="frame" title="入札情報">
                            <div style={{display:'flex'}}>
                                {
                                    bidGroup.groupStatus == 1 ?
                                    <div style={{padding:'8px', fontWeight:'bold', backgroundColor:'red', color:'#fff', width:'130px'}}><span>入札中</span></div>
                                    :
                                    <></>
                                }
                                {
                                    bidGroup.groupStatus == 8 ?
                                    <div style={{padding:'8px', fontWeight:'bold', backgroundColor:'#ffa500', color:'#fff', width:'130px'}}><span>開票中</span></div>
                                    :
                                    <></>
                                }
                                {
                                    bidGroup.groupStatus == 9 ?
                                        bidGroup.startTime ?
                                        <div style={{padding:'8px', fontWeight:'bold', backgroundColor:'#006400', color:'#fff', width:'130px'}}><span>落札済み</span></div>
                                        :
                                        <div style={{padding:'8px', fontWeight:'bold', backgroundColor:'#305496', color:'#fff', width:'130px'}}><span>確定売り</span></div>
                                    :
                                    <></>
                                }
                                <div style={{marginLeft:'8px'}}>
                                    <CustomOutlinedInput
                                        type  = "text"
                                        id    = "bidManager_bidGroupNo"
                                        label = "入札No"
                                        value = { bidGroup.entryNo }
                                        width = "90px"
                                    />
                                </div>
                                <div style={{marginLeft:'10px'}}>
                                    <CustomOutlinedInput
                                        type   = "text"
                                        id     = "bidManager_bidName"
                                        label  = "品名"
                                        value  = { bidGroup.bidName }
                                        width = "280px"
                                    />
                                </div>
                                {
                                    bidGroup.kgKubun == 1 ?
                                    <div style={{marginLeft:'10px'}}>
                                        <CustomOutlinedInput
                                            type  = "text"
                                            id    = "bidManager_groupSuryo"
                                            label = "合計Kg数量"
                                            value = { bidGroup.kgSuryo }
                                            width = "100px"
                                        />
                                    </div>
                                    :
                                    <div style={{marginLeft:'8px'}}>
                                        <CustomOutlinedInput
                                            type  = "text"
                                            id    = "bidManager_groupDNum"
                                            label = "合計数量"
                                            value = { bidGroup.suryo }
                                            width = "90px"
                                        />
                                    </div>
                                }
                                <div style={{marginLeft:'10px'}}>
                                    <CustomOutlinedInput
                                        type   = "text"
                                        id     = "bidManager_kgKubun"
                                        label  = "入札単位"
                                        value={ bidGroup.kgKubun == 1 ? 'Kg' : '箱' }
                                        width = "120px"
                                    />
                                </div>
                                <div style={{marginLeft:'10px'}}>
                                    <CustomOutlinedInput
                                        type   = "text"
                                        id     = "bidManager_danbe"
                                        label  = "水槽No"
                                        value={ bidGroup.danbe }
                                        width = "120px"
                                    />
                                </div>
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomDataGrid
                                    getRowClassName={(params) => {
                                        let className = ''
                                        if (params.indexRelativeToCurrentPage % 2 === 0) {
                                            className += 'even '
                                        } else {
                                            className += 'odd '
                                        }
                                        if (
                                            bidGroup.uniqueId &&
                                            params.row.uniqueId !== bidGroup.uniqueId &&
                                            (!bidGroup.bidName || bidGroup.bidName.includes('まとめ')) // セット入札で紐づけ魚種は赤文字で表示
                                        ) {
                                            className += 'cell_red '
                                        }
                                        return className.trim() // クラス名の文字列の前後の余分な空白を削除
                                    }}
                                    editMode                = "row"
                                    rows                    = { bidList }
                                    columns                 = { columns }
                                    sx                      = { {height:'150px',fontSize:14,borderColor: 'primary.light',} }
                                    density                 = "compact"
                                    localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                                    showColumnRightBorder
                                    showCellRightBorder 
                                    disableColumnMenu          = {true}
                                    disableRowSelectionOnClick
                                    hideFooter
                                    loading                 = { getDataInGroup_Loading }
                                />
                            </div>
                        </Frame>
                        <Frame classname="frame" title="入札状況">
                        <div style={{display:'flex'}}>
                            <div style={{marginBottom:'8px', marginLeft:'0px', display: authority == 3 ? 'none' : 'block'}}>
                                <Button
                                    color     = "secondary"
                                    style     = {{color:'#fff'}}
                                    size      = "midium"
                                    variant   = "contained"
                                    startIcon = {<UpdateIcon />}
                                    onClick   = {() => handleClick_update('1') }
                                >
                                更新
                                </Button>
                            </div>
                            <div style={{marginBottom:'8px', marginLeft:'20px'}}>
                                <CustomOutlinedInput
                                    type     = "text"
                                    id       = "bidManager_groupSuryoRemain"
                                    label    = "残り数量"
                                    value    = { groupSuryoRemain }
                                    onChange = {(e) => setGroupSuryoRemain(e.target.value)}
                                    width    = "100px"
                                />
                            </div>
                            <div style={{padding:'8px', fontWeight:'bold'}}>
                                <span>{unitName}</span>
                            </div>
                            <div style={{marginBottom:'8px', marginLeft:'10px'}}>
                                <CustomSelect
                                    id         = "userList_traderCode"
                                    label      = "登録者"
                                    width      = "180px"
                                    dataList   = {bidPersonList} 
                                    selectedId = {bidGroup.bidPersonCode} 
                                    onChange   = {(e) => handleChangexBidPerson(e.target.value)} 
                                />
                            </div>
                            <div style={{ display: authority == 3 ? 'none' : 'flex' }}>
                            {
                                bidGroup.groupStatus == 1 ?
                                <>
                                <div style={{marginBottom:'8px', marginLeft:'20px'}}>
                                    <Button
                                        color     = "secondary"
                                        style     = {{color:'#fff', backgroundColor:'#305496' }}
                                        size      = "midium"
                                        variant   = "contained"
                                        startIcon = {<LockOpenIcon />}
                                        onClick   = {() => setMessageInfo('confirm_vote', true) }
                                    >
                                    開票する
                                    </Button>
                                </div>
                                <div style={{marginBottom:'8px', marginLeft:'20px'}}>
                                    {
                                        !isRebid() ?
                                        <>
                                        <Button
                                            color     = "secondary"
                                            style     = {{color:'#fff', backgroundColor:'#ff0000'}}
                                            size      = "midium"
                                            variant   = "contained"
                                            startIcon = {<KeyOffIcon />}
                                            onClick   = { ()=> setMessageInfo('confirm', true) }
                                        >
                                        入札解除
                                        </Button>
                                        </>
                                        :<></>
                                    }
                                </div>
                                </>
                                :<></>
                            }
                            {
                                bidGroup.groupStatus == 8 ?
                                <>
                                <div style={{marginBottom:'8px', marginLeft:'20px'}}>
                                    <Button
                                        color     = "secondary"
                                        style     = {{color:'#fff', backgroundColor:'#305496' }}
                                        size      = "midium"
                                        variant   = "contained"
                                        startIcon = {<CheckIcon />}
                                        onClick   = {() => handleClick_Register_before() }
                                    >
                                    落札登録
                                    </Button>
                                </div>
                                <div style={{marginBottom:'8px', marginLeft:'20px'}}>
                                    <Button
                                        color     = "secondary"
                                        style     = {{color:'#fff', backgroundColor:'#ff0000'}}
                                        size      = "midium"
                                        variant   = "contained"
                                        startIcon = {<KeyOffIcon />}
                                        onClick   = { ()=> setMessageInfo('confirm_vote_release', true) }
                                    >
                                    開票解除
                                    </Button>
                                </div>
                                </>
                                :<></>
                            }
                            {
                                bidGroup.groupStatus == 9 ?
                                <>
                                <div style={{marginBottom:'8px', marginLeft:'20px', display: authority == 3 ? 'none' : 'block'}}>
                                    <Button
                                        color     = "secondary"
                                        style     = {{color:'#fff', backgroundColor:'#305496' }}
                                        size      = "midium"
                                        variant   = "contained"
                                        startIcon = {<CheckIcon />}
                                        onClick   = {() => handleClick_BidRestart() }
                                    >
                                    再入札
                                    </Button>
                                </div>
                                </>
                                :<></>
                            }
                                
                            </div>
                        </div>
                            <CustomDataGrid
                                getRowClassName={(params) =>
                                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                }
                                editMode                = "row"
                                rows                    = { bidHistoryList }
                                columns                 = { authority == 3 ? columns_history_trader : columns_history }
                                sx                      = { {height:dataGridHeight,fontSize:14,borderColor: 'primary.light',} }
                                density                 = "compact"
                                localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                                showColumnRightBorder
                                showCellRightBorder
                                hideFooter
                                checkboxSelection
                                disableRowSelectionOnClick = {true}
                                disableColumnMenu          = {true}
                                loading                    = { searchData_history_Loading }
                                onCellClick                = {(event) => handleClick_BidHistoryList(event)}
                                processRowUpdate           = {handleProcessRowUpdate}
                                onRowSelectionModelChange  = {(newSelectionModel) => { setCheckedBidHistoryList(newSelectionModel) }}
                                rowSelectionModel          = {checkedBidHistoryList}
                                isRowSelectable={(params) => params.row.bidStatus !== 9}
                            />
                        </Frame>
                    </div>
                </div>
                <CustomSnackBar
                    open            = {messageState.snackBarTrue}
                    onClose         = {() => setMessageInfo('snackBarTrue', false)}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "登録しました"
                />
                <CustomSnackBar
                    open            = {messageState.snackBarFalse}
                    onClose         = {() => setMessageInfo('snackBarFalse', false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = "登録に失敗しました"
                />
                <CustomSnackBar
                    open            = {messageState.require}
                    onClose         = {() => setMessageInfo('require', false)}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = {requireMessage}
                />
                <CustomConfirm
                    open      = {messageState.confirm}
                    onClose   = {() => setMessageInfo('confirm', false)}
                    onClickNG = {() => setMessageInfo('confirm', false)}
                    onClickOK = {() => handleClick_deleteGroup()}
                    message   = "入札解除を行います。よろしいですか？"
                />
                <CustomConfirm
                    open      = {messageState.confirm_vote}
                    onClose   = {() => setMessageInfo('confirm_vote', false)}
                    onClickNG = {() => setMessageInfo('confirm_vote', false)}
                    onClickOK = {() => handleClick_vote()}
                    message   = "開票します。よろしいですか？"
                />
                <CustomConfirm
                    open      = {messageState.confirm_vote_release}
                    onClose   = {() => setMessageInfo('confirm_vote_release', false)}
                    onClickNG = {() => setMessageInfo('confirm_vote_release', false)}
                    onClickOK = {() => handleClick_vote_release()}
                    message   = "開票解除を行います。よろしいですか？"
                />
                <CustomConfirm
                    open      = {messageState.confirm_register}
                    onClose   = {() => setMessageInfo('confirm_register', false)}
                    onClickNG = {() => setMessageInfo('confirm_register', false)}
                    onClickOK = {() => handleClick_Register()}
                    message   = "落札登録を行います。よろしいですか？"
                />
                <CustomConfirm
                    open      = {messageState.confirm_register_before}
                    onClose   = {() => setMessageInfo('confirm_register_before', false)}
                    onClickNG = {() => setMessageInfo('confirm_register_before', false)}
                    onClickOK = {() => handleClick_Register()}
                    message   = "残り数量があります。このまま落札登録を行ってよろしいですか？"
                />
            </div>
        </>
    )
}
export default BidManager;